/**
 * FactoringApprovalChip
 * Approval chip for expanded load detail panes in both Load Hub and AI Hub.
 *
 * Layout:
 *   ┌───────────────────────┐
 *   │ Triumph ⌄ │ ✓ Approved │
 *   └───────────────────────┘
 *
 * Left segment: provider name + ChevronDown (click opens dropdown)
 * Right segment: success/warning/danger soft bg with CircleCheck + grade/"Approved"
 *
 * Optional prefetch props let callers skip the API call when a grade is already
 * known (e.g. from socket data), and receive a callback when a fresh grade is fetched.
 *
 * `deferred` + `onReveal` turn the right segment into a "See Rating" button and make the chip inert
 * until the user asks — for surfaces where a panel opens far more often than its rating is read.
 */

import React, { useRef, useState, useEffect } from 'react'
import { CircleCheck, Link, LoaderCircle, Search } from 'lucide-react'
import { abbreviateRating, RATING_COLORS } from '@numeo/types'
import { FactoringProvider } from 'services/factoring'
import { factoringLookupIdentity, useFactoringData } from 'hooks/useFactoringData'
import { useFactoringProviderStore } from 'hooks/factoringProviderStore'
import { OutgoFactorableBadge } from 'components/loads/OutgoFactorableBadge'
import { FactoringAuthDialog } from 'components/FactoringAuthDialog'
import { cn } from 'utils/cn'
import { FACTORING_CHIP_PROVIDERS } from './factoringChipProviders'
import { FactoringProviderMenu } from './FactoringProviderMenu'

interface FactoringApprovalChipBaseProps {
    mcNumber: string | null | undefined
    /** Pre-fetched grade from search/poll response — skips the API call when provided */
    prefetchedGrade?: string
    /** Source label for the pre-fetched grade (e.g. 'RTS', 'TRIUMPH') */
    prefetchedSource?: string
    /** Called when a grade is freshly fetched via API so the parent can persist it */
    onGradeFetched?: (grade: string, source: string, loadsCount?: number) => void
    /**
     * Called when a lookup completes and the provider has no rating for this broker — a definitive
     * answer, not a failure. Separate from `onGradeFetched` because callers that persist a grade to a
     * row have nothing to persist here, while a caller tracking "did the operator get an answer" does.
     * Never fires for auth errors or an unavailable provider, which are not answers about the broker.
     */
    onLookupNotFound?: () => void
    /**
     * DAT per-load factorability — used when Outgo is the selected provider (the right segment
     * then shows Factorable / Not factorable from this flag). REQUIRED (not optional) on purpose:
     * the Outgo mode is derived from the global provider store, so any caller that forgot to pass
     * this would silently show "Not factorable" for every load. Making it required forces every
     * call site to supply it. Pass `load.details?.is_factorable` (CanonicalLoad) or
     * `load.is_factorable` (CanonicalSearchLoad).
     */
    isFactorable: boolean | null | undefined
    /** Restrict the provider dropdown to this subset (e.g. Box Truck shows TAFS, RTS and eCapital). One entry → the left
     *  segment is a static label, no dropdown. Omit → all providers. */
    providers?: FactoringProvider[]
    /** Use this provider regardless of the global store. Fetches for it
     *  without changing the app-wide provider. */
    forceProvider?: FactoringProvider
    /** Controlled chip-local provider selection. When supplied with forceProvider, dropdown changes stay
     *  local to the caller instead of mutating the app-wide provider store. */
    onProviderChange?: (provider: FactoringProvider) => void
    /** Broker company name — used as the rating lookup key when there's no MC (Sylectus box-truck loads).
     *  Only honoured with a `forceProvider` that supports it (RTS/TAFS). */
    companyName?: string | null
    /** Append the provider's numeric signal (TAFS credit score) after the status, e.g. "Approved · 93.9".
     *  Off by default so Load Hub / AI Hub chips are unchanged. */
    showScore?: boolean
}

/**
 * Hold the lookup until the user asks for it: the right segment becomes a "See Rating" button and NO
 * request is made — not the rating, not the status check. Absent by default, so Load Hub and AI Hub keep
 * fetching on mount exactly as before.
 *
 * The two travel together by type rather than by convention. `onReveal` used to be an independent
 * optional documented as "required in spirit", which meant a caller could pass `deferred` alone and ship
 * a See Rating button wired to `undefined`: no rating, no error, no way out, and the hook inert forever.
 * A union makes that uncompilable.
 *
 * The caller decides when to defer, because only the caller knows whether deferring is even useful (Box
 * Truck defers unless it has confirmed the provider is unlinked, since an unlinked one stops at its own
 * auth check and costs no upstream lookup).
 *
 * Deferring is implemented by handing `useFactoringData` a null lookup key, which is its existing
 * "nothing to look up" path — not a second skip mechanism to keep in sync with `skipFetch`.
 *
 * Note `deferred` is checked AHEAD of `showingPrefetched`: passing both it and `prefetchedGrade` would
 * hide a grade the caller already holds behind a button, and pressing it would spend a lookup that was
 * never needed. No caller does that today.
 */
type FactoringApprovalChipDeferral = { deferred: true; onReveal: () => void } | { deferred?: false; onReveal?: never }

type FactoringApprovalChipProps = FactoringApprovalChipBaseProps & FactoringApprovalChipDeferral

// Grade → tone. Derived from the shared `abbreviateRating` (@numeo/types) so the chip and the row badge
// can't disagree on which grades are good/bad (they both consume the ONE grade-string parser — no parallel
// copy to keep in sync, and no duplicate "NOT APPROVED before APPROVED" guard). We only translate its
// resolved color into the chip's semantic tone.
const gradeTone = (grade?: string): 'success' | 'warning' | 'danger' | 'muted' => {
    if (!grade || grade === '–') return 'muted'
    const { color } = abbreviateRating(grade)
    if (color === RATING_COLORS.GREEN) return 'success'
    if (color === RATING_COLORS.RED) return 'danger'
    if (color === RATING_COLORS.AMBER) return 'warning'
    return 'muted'
}

export const FactoringApprovalChip: React.FC<FactoringApprovalChipProps> = ({
    mcNumber,
    prefetchedGrade,
    prefetchedSource,
    onGradeFetched,
    onLookupNotFound,
    isFactorable,
    providers,
    forceProvider,
    onProviderChange,
    companyName,
    showScore,
    deferred,
    onReveal,
}) => {
    const [providerOverride, setProviderOverride] = useState(false)
    const providerList = providers ? FACTORING_CHIP_PROVIDERS.filter((provider) => providers.includes(provider.value)) : FACTORING_CHIP_PROVIDERS
    // Outgo has no broker rating — never fetch for it; the right segment comes from isFactorable. A forced
    // chip-local provider overrides the global store, so Outgo mode never applies to it.
    const storeProvider = useFactoringProviderStore((s) => s.provider)
    const capitalDepotPrefetched = prefetchedSource?.toLowerCase() === FactoringProvider.CAPITAL_DEPOT
    const effectiveForcedProvider = forceProvider ?? (!storeProvider && capitalDepotPrefetched ? FactoringProvider.CAPITAL_DEPOT : undefined)
    const providerForFetch = effectiveForcedProvider ?? storeProvider
    // Gated on the RAW prop. `effectiveForcedProvider` is synthetic on any row whose prefetched grade
    // came from Capital Depot, so gating on it disabled the dropdown for exactly those rows — the user
    // could no longer switch them to another provider, and Disconnect (rendered inside this dropdown)
    // became unreachable again. The transition it was guarding against is handled in useFactoringData
    // instead, which now marks itself initialized while forced so releasing forced mode cannot trigger
    // first-time provider auto-detection over the user's choice.
    const dropdownEnabled = providerList.length > 1 && (!forceProvider || !!onProviderChange)
    const isOutgo = !effectiveForcedProvider && storeProvider === FactoringProvider.OUTGO
    // Capital Depot's score is not part of the prefetched grade summary, so keep its provider fetch
    // active instead of abusing `factoring_loads_count` to carry the score through the parent row.
    const skipFetch = (!!prefetchedGrade && !providerOverride && providerForFetch !== FactoringProvider.CAPITAL_DEPOT) || isOutgo || !!deferred
    // BOTH keys have to be withheld, not just the MC: `useFactoringData` looks a broker up by company
    // name whenever the MC is absent (the Sylectus path), so nulling the MC alone would still fetch.
    const { selectedProvider, displayData, isLoading, authError, lookupError, outcomeIdentity, setProvider, refresh } = useFactoringData(
        skipFetch ? null : (mcNumber ?? null),
        {
            provider: effectiveForcedProvider,
            companyName: skipFetch ? null : companyName,
        }
    )
    // Set by the See Rating press below. Between that click and `useFactoringData`'s effect there is a
    // committed render with the real keys but no data yet — isLoading still false, rating still the '–'
    // sentinel — which fell through the status chain to a check-marked "Unknown". React 18 flushes
    // passive effects after paint, so the operator actually saw a verdict on the broker that nothing had
    // computed. Scoped to the deferred path, so no other surface changes.
    const [revealPending, setRevealPending] = useState(false)
    const hasAnswer = !!authError || !!lookupError || (!!displayData.rating && displayData.rating !== '–')
    const awaitingReveal = revealPending && !hasAnswer
    // WHICH lookup was reported, not merely whether one was. A boolean latch was correct only while a
    // provider change remounted the chip; Box Truck stopped doing that (the skeleton it used to swap in
    // was destroying the dropdown and the auth dialog), so the latch survived the switch and the second
    // provider's grade was never reported. The caller then had no record of a lookup it had paid for.
    // Shared by both report paths below, since one lookup resolves to a grade or to no-rating, never both.
    const reportedFor = useRef<string | null>(null)
    const [authDialogProvider, setAuthDialogProvider] = useState<FactoringProvider | null>(null)

    useEffect(() => {
        if (hasAnswer || isLoading) setRevealPending(false)
    }, [hasAnswer, isLoading])

    // Persist freshly fetched grade to parent so the row badge updates.
    //
    /**
     * Report an answer to the caller — once per lookup, and never one that belongs to a different lookup.
     *
     * `selectedProvider` is derived during render while the hook's answer is written asynchronously, so
     * there are commits where the two describe different requests: switching provider carries the old
     * grade under the new provider, and the auto-detect path does the reverse — it adopts a provider
     * BECAUSE an answer arrived, both in one commit. No heuristic on this side can tell those apart,
     * which is why the hook now stamps every outcome with the lookup it answers and this simply compares.
     *
     * Both outcomes share one effect and one latch deliberately: with two, whichever ran first would mark
     * the lookup reported and the second would skip an answer it should have delivered.
     */
    const lookupIdentity = factoringLookupIdentity(selectedProvider, mcNumber, companyName)
    useEffect(() => {
        if (skipFetch || isLoading || outcomeIdentity !== lookupIdentity) return
        if (reportedFor.current === lookupIdentity || authError) return

        // "No rating" is an answer about the broker; an auth error or an unavailable provider is not,
        // and must stay re-askable.
        if (lookupError === 'not_found') {
            if (!onLookupNotFound) return
            reportedFor.current = lookupIdentity
            onLookupNotFound()
            return
        }
        if (!onGradeFetched || !displayData.rating || displayData.rating === '–' || !selectedProvider) return
        reportedFor.current = lookupIdentity
        const loadsCount = displayData.loadsCount ? parseInt(displayData.loadsCount, 10) : undefined
        onGradeFetched(displayData.rating, selectedProvider, loadsCount || undefined)
    }, [
        skipFetch,
        onGradeFetched,
        onLookupNotFound,
        displayData.rating,
        displayData.loadsCount,
        selectedProvider,
        isLoading,
        authError,
        lookupError,
        lookupIdentity,
        outcomeIdentity,
    ])

    // Outgo factorability is per-load (MC-independent). Box Truck's RTS/TAFS/eCapital providers can resolve a broker
    // company name, so a companyName also satisfies the lookup when Sylectus supplies no MC.
    if (!mcNumber && !companyName && !isOutgo) {
        return (
            <div className="flex items-center h-9 px-3 rounded-2xl border border-[var(--lh-border)] text-[12px] text-[var(--lh-fg-muted)]">
                No MC number — factoring unavailable
            </div>
        )
    }

    // When prefetched (and the user hasn't overridden the provider yet), display the prefetched
    // source label in the dropdown button so the chip reflects the grade's actual origin.
    const showingPrefetched = !!prefetchedGrade && !providerOverride
    const prefetchProviderMatch = prefetchedSource ? FACTORING_CHIP_PROVIDERS.find((provider) => provider.value === prefetchedSource.toLowerCase()) : null
    const outgoProvider = FACTORING_CHIP_PROVIDERS.find((provider) => provider.value === FactoringProvider.OUTGO)
    const fallbackProvider = providerList[0] ?? FACTORING_CHIP_PROVIDERS[0]
    // Outgo takes precedence over a prefetched grade's source — the left label must match
    // the actually-selected provider so the chip never shows RTS on the left with Outgo picked.
    const currentProvider = isOutgo
        ? (outgoProvider ?? fallbackProvider)
        : showingPrefetched && prefetchProviderMatch
          ? prefetchProviderMatch
          : providerList.find((provider) => provider.value === selectedProvider) || fallbackProvider

    const grade = showingPrefetched ? prefetchedGrade : displayData.rating
    // Tone precedence: Outgo's per-load flag → the backend-resolved tone (OTR decides its own color
    // server-side) → the grade-string fallback for providers that don't supply a tone.
    const tone = isOutgo ? (isFactorable === true ? 'success' : 'muted') : (displayData.statusTone ?? gradeTone(grade))

    const toneClasses = {
        success: 'bg-[var(--lh-success-soft)] text-[var(--lh-success)]',
        warning: 'bg-[var(--lh-warning-soft)] text-[var(--lh-warning)]',
        danger: 'bg-[var(--lh-danger-soft)] text-[var(--lh-danger)]',
        muted: 'bg-[var(--lh-surface-secondary)] text-[var(--lh-fg-muted)]',
    }[tone]

    const gradeText = (grade || '').trim()
    const gradeAlreadyDescriptive = /APPROVED|BUY|CALL|DENIED|NO BUY|NOT FOUND|REJECTED/i.test(gradeText)
    const rightLabel = isOutgo
        ? isFactorable === true
            ? 'Factorable'
            : 'Not factorable'
        : isLoading
          ? 'Loading…'
          : authError
            ? 'Connect'
            : lookupError === 'not_found'
              ? 'No rating'
              : lookupError === 'unavailable'
                ? 'Unavailable'
                : gradeText && gradeText !== '–'
                  ? tone === 'success'
                      ? gradeAlreadyDescriptive
                          ? gradeText
                          : `Approved · ${gradeText}`
                      : gradeText
                  : 'Unknown'

    return (
        <>
            <div className="flex items-stretch h-9 rounded-2xl border border-[var(--lh-border)] overflow-hidden text-[12px]">
                <FactoringProviderMenu
                    currentProvider={currentProvider}
                    enabled={dropdownEnabled}
                    providers={providerList}
                    selectedProvider={selectedProvider}
                    onSelect={(provider) => {
                        setProviderOverride(true)
                        if (forceProvider && onProviderChange) onProviderChange(provider)
                        else setProvider(provider)
                    }}
                    // Withheld while deferred: `refresh` closes over the null keys this chip hands the
                    // hook, so it would reach fetchForcedProvider's "nothing to look up" return — no
                    // request, no spinner, no change, with See Rating still sitting there. The button
                    // beside it is the control that actually fetches.
                    onRefresh={deferred ? undefined : refresh}
                />

                {/* Right segment — approval status */}
                {isOutgo ? (
                    <div className={cn('flex-1 flex items-center justify-center gap-1.5 px-3 font-medium', toneClasses)}>
                        {isFactorable === true && <OutgoFactorableBadge size={14} withTooltip={false} />}
                        {rightLabel}
                    </div>
                ) : deferred ? (
                    // Deferred sits ahead of every data-derived branch on purpose: with nothing fetched,
                    // authError/isLoading/lookupError are all falsy and the chain would otherwise fall
                    // through to "Unknown" — a verdict we have not asked for.
                    <button
                        type="button"
                        onClick={() => {
                            setRevealPending(true)
                            onReveal?.()
                        }}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 font-medium cursor-pointer border-none bg-[var(--lh-accent-soft)] text-[var(--lh-accent)] hover:bg-[var(--lh-accent-soft-hover)] transition-colors"
                    >
                        <Search aria-hidden className="h-3.5 w-3.5" />
                        See Rating
                    </button>
                ) : isLoading || awaitingReveal ? (
                    <div className={cn('flex-1 flex items-center justify-center gap-1.5 px-3 font-medium', toneClasses)}>
                        <LoaderCircle aria-hidden className="h-3 w-3 animate-spin" />
                        Loading…
                    </div>
                ) : authError ? (
                    <button
                        type="button"
                        onClick={() => setAuthDialogProvider(forceProvider || selectedProvider || FactoringProvider.RTS)}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 font-medium cursor-pointer border-none bg-[var(--lh-accent-soft)] text-[var(--lh-accent)] hover:bg-[var(--lh-accent-soft-hover)] transition-colors"
                    >
                        <Link aria-hidden className="h-3.5 w-3.5" />
                        Connect
                    </button>
                ) : lookupError ? (
                    // Lookup failures stay neutral and text-only: not-found and an unavailable provider
                    // must never inherit an approval tint or check icon.
                    <div className="flex-1 flex items-center justify-center px-3 font-medium text-[var(--lh-fg-muted)] bg-[var(--lh-surface-secondary)]">
                        {rightLabel}
                    </div>
                ) : (
                    <div className={cn('flex-1 flex items-center justify-center gap-1 px-2.5 font-medium whitespace-nowrap', toneClasses)}>
                        <CircleCheck aria-hidden className="h-3.5 w-3.5" />
                        {rightLabel}
                        {showScore && currentProvider.value === FactoringProvider.TAFS && displayData.loadsCount && (
                            <span className="opacity-70">· {displayData.loadsCount}</span>
                        )}
                        {(currentProvider.value === FactoringProvider.CAPITAL_DEPOT || currentProvider.value === FactoringProvider.ECAPITAL) &&
                            displayData.creditScore && <span className="opacity-70">· {displayData.creditScore}</span>}
                    </div>
                )}
            </div>

            {authDialogProvider && (
                <FactoringAuthDialog
                    open
                    onClose={() => setAuthDialogProvider(null)}
                    provider={authDialogProvider}
                    tokenPurpose="rating"
                    onSuccess={() => {
                        if (authDialogProvider) {
                            if (forceProvider && onProviderChange) void refresh()
                            else setProvider(authDialogProvider)
                        }
                        setAuthDialogProvider(null)
                    }}
                />
            )}
        </>
    )
}
